// src/components/features/Vehicle/VehiclePickerSheet/VehiclePickerSheet.tsx
// Bottom sheet for selecting a vehicle on mobile.
// Shows all available vehicles with image, name, subtitle, and highlights
// the currently selected one.

import React, { useCallback } from "react";
import {
  Box,
  Drawer,
  Group,
  Stack,
  Text,
  UnstyledButton,
  Paper,
  ScrollArea,
  Divider,
  CloseButton,
  useMantineTheme,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import {
  getCarDisplayName,
  getCarSubtitle,
} from "@/utils/carHelpers";
import { Car } from "@/utils/api/CarService";

import { AppIcon } from "@/components/generic/AppIcon";

import { VehicleImage } from "../VehicleImage";

// ============================================
// Types
// ============================================

export interface VehiclePickerSheetProps {
  /** Whether the sheet is open */
  opened: boolean;

  /** Close handler */
  onClose: () => void;

  /** List of available cars */
  cars: Car[];

  /** Currently selected car ID */
  selectedCarId: string | null;

  /** Called when a vehicle is selected */
  onSelect: (carId: string) => void;

  /** Called when "Add Vehicle" is tapped */
  onAddVehicle?: () => void;
}

// ============================================
// Component
// ============================================

export const VehiclePickerSheet: React.FC<VehiclePickerSheetProps> = ({
  opened,
  onClose,
  cars,
  selectedCarId,
  onSelect,
  onAddVehicle,
}) => {
  const theme = useMantineTheme();
  const { t } = useTranslation(["common"]);

  const handleSelect = useCallback(
    (carId: string) => {
      onSelect(carId);
      onClose();
    },
    [onSelect, onClose]
  );

  const handleAddVehicle = useCallback(() => {
    onClose();
    onAddVehicle?.();
  }, [onClose, onAddVehicle]);

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="bottom"
      size="auto"
      withCloseButton={false}
      overlayProps={{ backgroundOpacity: 0.3 }}
      transitionProps={{ transition: "slide-up", duration: 250 }}
      styles={{
        content: {
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          maxHeight: "70vh",
        },
        body: {
          padding: 0,
        },
      }}
    >
      {/* Drag handle */}
      <Box pt="sm" pb="xs" px="md">
        <Box
          w={40}
          h={4}
          bg="neutral.3"
          mx="auto"
          style={{ borderRadius: 2 }}
        />
      </Box>

      {/* Header */}
      <Group px="lg" pb="md" gap="sm" justify="space-between">
        <Text fw={700} size="lg" c="neutral.8">
          {t("common:dashboard.vehiclePicker.title", {
            defaultValue: "Select Vehicle",
          })}
        </Text>
        <CloseButton size="lg" onClick={onClose} />
      </Group>

      <Divider color="neutral.1" />

      {/* Vehicle List */}
      <ScrollArea.Autosize mah="50vh" px="md" py="md">
        <Stack gap="xs">
          {cars.map((car) => {
            const carId = car.id || "";
            const isSelected = carId === selectedCarId;
            const displayName = getCarDisplayName(car);
            const subtitle = getCarSubtitle(car);

            return (
              <UnstyledButton
                key={carId}
                onClick={() => handleSelect(carId)}
                style={{
                  borderRadius: theme.radius.md,
                  transition: "background-color 150ms ease",
                }}
              >
                <Paper
                  p="md"
                  radius="md"
                  bg={isSelected ? "brand.0" : "neutral.0"}
                  style={{
                    border: `1px solid ${isSelected
                      ? theme.colors.brand[2]
                      : theme.colors.neutral[2]
                      }`,
                  }}
                >
                  <Group gap="md" wrap="nowrap">
                    <VehicleImage
                      car={car}
                      size="sm"
                      radius="md"
                      alt={displayName}
                    />

                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Text
                        fw={600}
                        size="md"
                        c={isSelected ? "brand.7" : "neutral.8"}
                        truncate
                      >
                        {displayName}
                      </Text>
                      {subtitle && (
                        <Text size="sm" c="neutral.5" truncate lh={1.3}>
                          {subtitle}
                        </Text>
                      )}
                    </Box>

                    {isSelected && (
                      <AppIcon
                        iconName="Check"
                        size={20}
                        color={theme.colors.brand[6]}
                      />
                    )}
                  </Group>
                </Paper>
              </UnstyledButton>
            );
          })}

          {/* Add Vehicle Option */}
          {onAddVehicle && (
            <>
              <Divider color="neutral.1" my="xs" />
              <UnstyledButton
                onClick={handleAddVehicle}
                style={{
                  borderRadius: theme.radius.md,
                  transition: "background-color 150ms ease",
                }}
              >
                <Paper
                  p="md"
                  radius="md"
                  bg="neutral.0"
                  style={{
                    border: `1px dashed ${theme.colors.neutral[3]}`,
                  }}
                >
                  <Group gap="md" wrap="nowrap">
                    <Box
                      w={56}
                      h={56}
                      style={{
                        borderRadius: `var(--mantine-radius-md)`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: theme.colors.brand[0],
                      }}
                    >
                      <AppIcon
                        iconName="Plus"
                        size={24}
                        color={theme.colors.brand[6]}
                      />
                    </Box>

                    <Text fw={600} size="md" c="brand.6">
                      {t("common:dashboard.vehiclePicker.addVehicle", {
                        defaultValue: "Add Vehicle",
                      })}
                    </Text>
                  </Group>
                </Paper>
              </UnstyledButton>
            </>
          )}
        </Stack>
      </ScrollArea.Autosize>

      {/* Bottom safe area */}
      <Box h={20} />
    </Drawer>
  );
};